function toFiniteNumber(value, fallback = Number.NEGATIVE_INFINITY) {
	const parsed = Number.parseInt(value, 10);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeElement(target) {
	if (!target) {
		return null;
	}
	if (target.$el) {
		return target.$el;
	}
	if (typeof Element !== 'undefined' && target instanceof Element) {
		return target;
	}
	return null;
}

export function resolveZIndex(targets, fallback = 0) {
	const source = Array.isArray(targets) ? targets : [targets];
	let maxZIndex = Number.NEGATIVE_INFINITY;
	source.forEach((target) => {
		const element = normalizeElement(target);
		if (!element || typeof window === 'undefined' || !window.getComputedStyle) {
			return;
		}
		maxZIndex = Math.max(maxZIndex, toFiniteNumber(window.getComputedStyle(element).zIndex));
	});
	return Number.isFinite(maxZIndex) ? maxZIndex : fallback;
}

export function createDismissStack() {
	let orderSeed = 0;
	const entries = new Map();

	function safeCall(fn, fallback) {
		if (typeof fn !== 'function') {
			return fallback;
		}
		try {
			const value = fn();
			return value === undefined ? fallback : value;
		} catch (error) {
			return fallback;
		}
	}

	function getOpenEntries() {
		return Array.from(entries.values())
			.filter((entry) => Boolean(safeCall(entry.isOpen, false)))
			.sort((prev, next) => {
				const zIndexDiff = safeCall(next.getZIndex, 0) - safeCall(prev.getZIndex, 0);
				if (zIndexDiff !== 0) {
					return zIndexDiff;
				}
				return next.order - prev.order;
			});
	}

	return {
		register(entry = {}) {
			const id = entry.id || `dismiss-entry-${orderSeed + 1}`;
			const normalizedEntry = {
				id,
				order: ++orderSeed,
				isOpen: typeof entry.isOpen === 'function' ? entry.isOpen : () => Boolean(entry.isOpen),
				getZIndex: typeof entry.getZIndex === 'function' ? entry.getZIndex : () => entry.zIndex || 0,
				close: typeof entry.close === 'function' ? entry.close : () => {}
			};
			entries.set(id, normalizedEntry);
			return () => {
				if (entries.get(id) === normalizedEntry) {
					entries.delete(id);
				}
			};
		},
		dismissTop() {
			const topEntry = getOpenEntries()[0];
			if (!topEntry) {
				return false;
			}
			safeCall(topEntry.close, undefined);
			return true;
		},
		getTopEntry() {
			return getOpenEntries()[0] || null;
		},
		clear() {
			entries.clear();
		}
	};
}

export const dismissStack = createDismissStack();
